import React from 'react'
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Stack, Typography, Alert } from '@mui/material'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import AdSlot from '../components/AdSlot'
import VideoCard, { Video } from '../components/VideoCard'
import { useInView } from '../lib/useInView'
import { maybeAttachAuthorization } from '@mahendran/shared-frontend-config'
import { buildApiUrl } from '../lib/apiBase'
import { VideoSeries, VideoSuggestion } from '../types/video'

function extractYoutubeId(url: string): string | null {
    try {
        const u = new URL(url)
        if (u.hostname.includes('youtube.com')) {
            const v = u.searchParams.get('v'); if (v) return v
            const parts = u.pathname.split('/').filter(Boolean)
            const idx = parts.indexOf('embed'); if (idx >= 0 && parts[idx + 1]) return parts[idx + 1]
        }
        if (u.hostname.includes('youtu.be')) { const id = u.pathname.replace('/', ''); if (id) return id }
    } catch { }
    return null
}

const FadeIn: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.15 })
    return (
        <Box ref={ref} sx={{ opacity: inView ? 1 : 0, transform: inView ? 'none' : 'translateY(12px)', transition: 'opacity 480ms ease, transform 480ms ease' }}>
            {children}
        </Box>
    )
}

const SeriesCard: React.FC<{ series: VideoSeries & { videoCount: number }, onClick: () => void }> = ({ series, onClick }) => {
    const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.15 })
    const { t } = useTranslation()

    // Use teaser image if available, otherwise fallback to placeholder
    const coverImage = series.teaserImageUrl || `https://picsum.photos/seed/series-${series.slug}/800/450`

    return (
        <Box ref={ref} sx={{ opacity: inView ? 1 : 0, transform: inView ? 'none' : 'translateY(12px)', transition: 'opacity 480ms ease, transform 480ms ease' }}>
            <Box component="button" type="button" aria-label={series.title} sx={{
                width: '100%', border: 0, p: 0, textAlign: 'left', bgcolor: 'transparent',
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 3,
                cursor: 'pointer',
                transition: 'transform 200ms ease, box-shadow 200ms ease',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: 8 }
            }} onClick={onClick}>
                <img src={coverImage} alt={series.title} style={{ width: '100%', display: 'block' }} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient( to top, rgba(0,0,0,0.6), rgba(0,0,0,0.05) )' }} />
                <Box sx={{ position: 'absolute', bottom: 12, left: 12, color: 'white' }}>
                    <Typography variant="h6" fontWeight={700}>{series.title}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {series.videoCount} {t('series.videos', 'Videos')}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}

const fetchWithAuth = async (url: string) => {
    const headers = new Headers();
    const credentials = 'include';
    maybeAttachAuthorization(headers, credentials);
    const res = await fetch(url, { headers, credentials });
    return res;
}

const Home: React.FC = () => {
    const [latest, setLatest] = React.useState<Video[]>([])
    const [series, setSeries] = React.useState<(VideoSeries & { videoCount: number })[]>([])
    const [loadingSeries, setLoadingSeries] = React.useState(true)
    const [loadingVideos, setLoadingVideos] = React.useState(true)
    const [videoError, setVideoError] = React.useState(false)
    const [seriesError, setSeriesError] = React.useState(false)
    const [videoAttempt, setVideoAttempt] = React.useState(0)
    const [seriesAttempt, setSeriesAttempt] = React.useState(0)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()
    const [tags, setTags] = React.useState<string[]>(['all'])
    const activeTag = searchParams.get('tag') || 'all'
    const [savedTag] = React.useState(() => {
        try { return localStorage.getItem('home_tag') || 'all' } catch { return 'all' }
    })
    const selectTag = (tag: string) => {
        const next = new URLSearchParams(searchParams)
        if (tag === 'all') next.delete('tag')
        else next.set('tag', tag)
        setSearchParams(next, { replace: true })
    }
    const [confirmation, setConfirmation] = React.useState<null | {
        title: string
        youtubeUrl: string
        tags?: string
        description?: string
        seriesTitle?: string
    }>(null)
    const [confirmationOpen, setConfirmationOpen] = React.useState(false)

    // A shared URL wins over the remembered selection. Restore storage only on mount.
    React.useEffect(() => {
        if (!searchParams.has('tag') && savedTag !== 'all') selectTag(savedTag)
    }, [])

    React.useEffect(() => {
        try { localStorage.setItem('home_tag', activeTag) } catch { }
    }, [activeTag])

    React.useEffect(() => {
        if (!loadingVideos && !videoError && !tags.includes(activeTag)) selectTag('all')
    }, [loadingVideos, videoError, tags, activeTag])

    const filteredLatest = activeTag === 'all' ? latest : latest.filter(v => v.tags?.includes(activeTag))
    const filteredSeries = activeTag === 'all' ? series : series.filter(s => {
        // Filter series based on tags from their videos
        return latest.some(v => v.tags?.some(tag => tag === activeTag) && v.series === s.slug)
    })

    const onOpenVideo = (id: string) => navigate(`/video/${id}`)
    const onOpenSeries = (slug: string) => navigate(`/videos?series=${encodeURIComponent(slug)}`)

    const submissionSuccess = (location.state as { submissionSuccess?: typeof confirmation } | undefined)?.submissionSuccess

    React.useEffect(() => {
        if (submissionSuccess) {
            setConfirmation(submissionSuccess)
            setConfirmationOpen(true)
            navigate(`${location.pathname}${location.search}`, { replace: true })
        }
    }, [submissionSuccess, navigate, location.pathname, location.search])

    // Load videos
    React.useEffect(() => {
        let active = true
        setLoadingVideos(true)
        setVideoError(false)
        void (async () => {
            try {
                const res = await fetchWithAuth(buildApiUrl('/video-suggestions/approved'))
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                const data: VideoSuggestion[] = await res.json()
                const vids: Video[] = data.map(s => {
                    const yt = extractYoutubeId(s.youtubeUrl)
                    const thumb = yt ? `https://img.youtube.com/vi/${yt}/hqdefault.jpg` : 'https://picsum.photos/seed/fallback/600/338'
                    const tags = (s.tags || '').split(',').map(t => t.trim()).filter(Boolean)
                    return {
                        id: String(s.id),
                        title: s.title,
                        thumbnailUrl: thumb,
                        duration: '',
                        tags,
                        series: s.series?.slug
                    }
                })
                if (!active) return
                setLatest(vids)
                const tagSet = new Set<string>()
                vids.forEach(v => v.tags?.forEach(t => tagSet.add(t)))
                setTags(['all', ...Array.from(tagSet).sort()])
            } catch {
                if (active) { setLatest([]); setVideoError(true) }
            } finally {
                if (active) setLoadingVideos(false)
            }
        })()
        return () => { active = false }
    }, [videoAttempt])

    // Load series
    React.useEffect(() => {
        let active = true
        setSeriesError(false)
        void (async () => {
            try {
                setLoadingSeries(true)
                const res = await fetchWithAuth(buildApiUrl('/video-series/public'))
                if (!res.ok) throw new Error(`HTTP ${res.status}`)
                const data: VideoSeries[] = await res.json()

                // Get video counts for each series
                const seriesWithCounts = await Promise.all(
                    data.map(async (s) => {
                        try {
                            const videosRes = await fetchWithAuth(buildApiUrl(`/video-suggestions/approved/series/${encodeURIComponent(s.slug)}`))
                            const videoCount = videosRes.ok ? (await videosRes.json()).length : 0
                            return { ...s, videoCount }
                        } catch {
                            return { ...s, videoCount: 0 }
                        }
                    })
                )

                if (active) setSeries(seriesWithCounts)
            } catch (error) {
                console.error('Failed to load series:', error)
                if (active) { setSeries([]); setSeriesError(true) }
            } finally {
                if (active) setLoadingSeries(false)
            }
        })()
        return () => { active = false }
    }, [seriesAttempt])

    return (
        <Box sx={{ py: 4 }}>
            {/* Hero */}
            <Box sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 3,
                color: 'white',
                p: { xs: 3, md: 6 },
                mb: { xs: 3, md: 4 },
                boxShadow: '0 20px 60px rgba(118,75,162,0.35)',
                backgroundSize: '200% 200%',
                animation: 'heroGradient 12s ease infinite'
            }}>
                <Typography variant="h3" fontWeight={700} gutterBottom>
                    {t('hero.title', 'Lerne. Erschaffe. Teile.')}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 720 }} gutterBottom>
                    {t('hero.subtitle', 'Video-Serien von Visa-Tutorials bis Webentwicklung – modern präsentiert.')}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                    <Button variant="contained" color="secondary" onClick={() => navigate('/submit')}>{t('hero.ctaPrimary', 'Video vorschlagen')}</Button>
                    <Button variant="outlined" color="inherit" onClick={() => navigate('/videos')}>{t('hero.ctaSecondary', 'Alle Videos')}</Button>
                </Stack>
            </Box>

            {/* Top Ad */}
            <AdSlot slotName="top-leaderboard" height={120} />

            {/* Series */}
            <Box sx={{ mt: 4, mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h5" fontWeight={700}>{t('series.title', 'Serien')}</Typography>
                    <Stack direction="row" spacing={1}>
                        {tags.map(tag => (
                            <Chip
                                key={tag}
                                label={tag}
                                variant={activeTag === tag ? 'filled' : 'outlined'}
                                color={activeTag === tag ? 'secondary' : 'default'}
                                clickable
                                onClick={() => selectTag(tag)}
                            />
                        ))}
                    </Stack>
                </Stack>
                {loadingSeries && <Typography role="status">Serien werden geladen…</Typography>}
                {seriesError && <Alert severity="error" action={<Button onClick={() => setSeriesAttempt(value => value + 1)}>Serien erneut laden</Button>}>Serien konnten nicht geladen werden.</Alert>}
                {!loadingSeries && !loadingVideos && !seriesError && filteredSeries.length === 0 && <Typography>Keine Serien vorhanden.</Typography>}
                <Grid container spacing={{ xs: 2, md: 3 }}>
                    {!loadingSeries && filteredSeries.map((series) => (
                        <Grid key={series.id} item xs={12} md={4}>
                            <SeriesCard series={series} onClick={() => onOpenSeries(series.slug)} />
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Mid Ad */}
            <Box sx={{ my: { xs: 3, md: 4 } }}>
                <AdSlot slotName="mid-rectangle" height={250} />
            </Box>

            {/* Latest */}
            <Box sx={{ mt: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h5" fontWeight={700}>{t('latest.title', 'Neueste Videos')}</Typography>
                    <Button onClick={() => navigate('/videos')}>{t('latest.viewAll', 'Alle ansehen')}</Button>
                </Stack>
                {loadingVideos && <Typography role="status">Videos werden geladen…</Typography>}
                {videoError && <Alert severity="error" action={<Button onClick={() => setVideoAttempt(value => value + 1)}>Videos erneut laden</Button>}>Videos konnten nicht geladen werden.</Alert>}
                {!loadingVideos && !videoError && filteredLatest.length === 0 && <Typography>Noch keine freigegebenen Videos.</Typography>}
                <Grid container spacing={{ xs: 2, md: 3 }}>
                    {filteredLatest.map(v => (
                        <Grid key={v.id} item xs={12} sm={6} md={3}>
                            <FadeIn>
                                <VideoCard video={v} onClick={onOpenVideo} />
                            </FadeIn>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Bottom Ad */}
            <Box sx={{ my: 4 }}>
                <AdSlot slotName="bottom-leaderboard" height={120} />
            </Box>
            <Dialog open={confirmationOpen} onClose={() => setConfirmationOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{t('submit.confirmation.title', 'Danke für deinen Vorschlag!')}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {t('submit.confirmation.body', 'Hey, wir haben deinen Vorschlag angenommen. Nach einer kurzen Prüfung geben wir ihn frei.')}
                    </DialogContentText>
                    {confirmation && (
                        <Stack spacing={1.5}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">{t('submit.fields.title', 'Titel')}</Typography>
                                <Typography variant="body1">{confirmation.title}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">YouTube</Typography>
                                <Typography variant="body1">{confirmation.youtubeUrl}</Typography>
                            </Box>
                            {confirmation.tags && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">{t('submit.fields.tags', 'Tags')}</Typography>
                                    <Typography variant="body1">{confirmation.tags}</Typography>
                                </Box>
                            )}
                            {confirmation.seriesTitle && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">{t('submit.fields.series', 'Serie')}</Typography>
                                    <Typography variant="body1">{confirmation.seriesTitle}</Typography>
                                </Box>
                            )}
                            {confirmation.description && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">{t('submit.fields.description', 'Beschreibung')}</Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{confirmation.description}</Typography>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmationOpen(false)} autoFocus>{t('common.ok', 'Alles klar')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default Home
